import { createFileRoute } from "@tanstack/react-router";
import { ProductPage } from "@/components/site/ProductPage";
import { productHowToJsonLd, productArticleJsonLd } from "@/components/site/ProductPage.seo";
import { AirbnbStrExtras } from "@/components/site/AirbnbStrExtras";
import { AirbnbStrDeepExtras } from "@/components/site/AirbnbStrDeepExtras";
import { StrRevenueCalculator } from "@/components/site/StrRevenueCalculator";
import { StrBreakEvenCalculator } from "@/components/site/StrBreakEvenCalculator";
import { StrReportCtaBanner } from "@/components/site/StrReportCtaBanner";
import { getProduct } from "@/data/products";
import { getProductHero } from "@/data/productHeroes";
import { buildHead } from "@/lib/seo-head";
import { LiveDscrRateSection } from "@/components/rates/LiveDscrRateSection";

const SLUG = "airbnb-short-term-rental-loans";
const PRODUCT = getProduct(SLUG)!;
const HERO = getProductHero(SLUG)!;
const URL = `/${SLUG}`;

// National-level focus keyword + supporting short/long-tail variations.
const FOCUS_KEYWORD = "Airbnb loans";
const KEYWORDS = [
  FOCUS_KEYWORD,
  "short term rental loan",
  "STR DSCR loan",
  "vacation rental mortgage",
  "AirDNA DSCR loan",
  "Vrbo loan",
  "how to finance an Airbnb",
  "STR loan requirements",
  "short term rental mortgage rates",
  "Airbnb refinance",
  "STR cash out refinance",
  "LLC Airbnb loan",
  "no tax return STR loan",
  "1-4 unit short term rental loan",
  "business purpose STR mortgage",
].join(", ");

const TITLE = "Airbnb Loans & Short-Term Rental Mortgages";
const DESCRIPTION =
  "Airbnb loans and short-term rental mortgages qualified on nightly-rental revenue (AirDNA or T-12 PMS), not tax returns. 20% down, 680+ FICO, LLC-friendly.";

export const Route = createFileRoute("/airbnb-short-term-rental-loans/")({
  head: () =>
    buildHead({
      title: TITLE,
      description: DESCRIPTION,
      url: URL,
      keywords: KEYWORDS,
      image: HERO.src,
      imageAlt: HERO.alt,
      speakableSelectors: [".tldr", ".faq"],
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "Airbnb / Short-Term Rental Loans", url: URL },
      ],
      service: { name: PRODUCT.name },
      faqs: PRODUCT.faq.map((f) => ({ q: f.q, a: f.a })),
      extraJsonLd: [
        productHowToJsonLd(PRODUCT, URL),
        productArticleJsonLd(PRODUCT, URL),
      ],
    }),
  component: () => (
    <ProductPage
      product={PRODUCT}
      hideBaseTools
      topTools={
        <>
          <StrReportCtaBanner variant="full" />
          <StrRevenueCalculator productKey="airbnb" />
          <LiveDscrRateSection
            strategyCards
            id="live-rates"
            eyebrow={"Live pricing · Rentals"}
            heading={"Live long-term DSCR pricing for a rental scenario"}
            paragraphs={[
              "These are actual pricing responses returned for a sample business-purpose DSCR scenario on non-owner-occupied investment property - rate, APR, borrower points or lender credit, estimated principal and interest, and lock period from the same quote. Pricing changes intraday and is not a loan approval, commitment, or guarantee of financing.",
            ]}
            links={[
              { to: "/investment-property-rates", label: "Price your own DSCR scenario" },
              { to: "/rates", label: "See today's investor rate bands" },
            ]}
          />
        </>
      }
      extraTools={
        <>
          <StrBreakEvenCalculator productKey="airbnb" />
          <StrReportCtaBanner variant="inline" />
          <AirbnbStrExtras />
          <AirbnbStrDeepExtras />
        </>
      }

    />
  ),
});
